// react
import React, { useMemo } from 'react';
import styled from 'styled-components';

// components
import { ArticleCard, Button, Title } from '@/components/atoms';
import { CardContainer } from '@/components/templates';

// store
import { Article } from '@karehero/models';

// hooks/
import { useWindowSize } from '@/hooks/';

// theme
import theme from '@/theme';

/**
 * ArticleFiltered shows a featured article and a list of articles.
 */
export const ArticleBookmarks = ({
  articles,
  isLoading,
  onBookmark,
  toNavigateBack,
}: ArticleBookmarksProps) => {
  // hooks/
  const [width] = useWindowSize();

  // memos
  const maxColumns = useMemo(() => {
    if (width < theme.breakpoint.mdValue) return 1;
    if (width < theme.breakpoint.lgValue) return 2;
    if (width < theme.breakpoint.xlValue) return 3;
    return 4;
  }, [width]);

  return (
    <Wrapper>
      <div>
        <Button
          ariaLabel='Back'
          variant='secondary'
          iconLeft={'chevron-left'}
          to={toNavigateBack}
          isInline
        >
          Back
        </Button>
      </div>
      <Title isNoMargin>My Bookmarks</Title>
      {articles?.length === 0 && <NoData>No bookmarks yet</NoData>}
      <CardContainer maxColumns={maxColumns}>
        {articles?.map((article, i) => (
          <ArticleCard
            key={i}
            article={article}
            onBookmark={onBookmark}
            isLoading={isLoading}
            isBookmarked
          />
        ))}
      </CardContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${(props) => props.theme.spacing[32]};
`;

const NoData = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  color: ${(props) => props.theme.color.neutral[80]};
  background: ${(props) => props.theme.color.neutral[30]};
  border-radius: 8px;
  font: ${(props) => props.theme.typography.body.md.regular};
`;

interface ArticleBookmarksProps {
  className?: string;
  articles?: Partial<Article>[];
  isLoading: boolean;
  onBookmark?: (article: Partial<Article>) => void;
  toNavigateBack?: string;
}

ArticleBookmarks.displayName = 'ArticleBookmarks';

export default ArticleBookmarks;
